import React, { useState, useEffect, useRef } from 'react';

const ClosureCommentModal = ({
    isOpen,
    taskTitle,
    isMeeting = false,
    onConfirm,
    onCancel,
    isSubmitting = false
}) => {
    const [comment, setComment] = useState('');
    const textareaRef = useRef(null);

    // Auto-focus textarea when modal opens
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isOpen]);

    // Reset comment when modal opens
    useEffect(() => {
        if (isOpen) {
            setComment('');
        }
    }, [isOpen]);

    const MIN_COMMENT_LENGTH = 25;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (comment.trim().length < MIN_COMMENT_LENGTH || isSubmitting) return;
        onConfirm(comment.trim());
    };

    const charCount = comment.trim().length;
    const isValidLength = charCount >= MIN_COMMENT_LENGTH;

    const handleCancel = () => {
        setComment('');
        onCancel();
    };

    if (!isOpen) return null;

    const instructionText = isMeeting
        ? 'Please provide meeting outcomes to proceed:'
        : 'Please provide a closure comment to proceed:';

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-[70]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                <div className="px-6 py-4 border-b">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Closing: "{taskTitle}"
                        </h2>
                        <button
                            onClick={handleCancel}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSubmitting}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <p className="text-sm text-gray-600 mb-3">
                        {instructionText}
                    </p>
                    <textarea
                        ref={textareaRef}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 resize-none"
                        rows={4}
                        placeholder={isMeeting ? 'Enter meeting outcomes...' : 'Enter closure comment...'}
                        disabled={isSubmitting}
                    />
                    <p className={`text-xs mt-1 ${isValidLength ? 'text-gray-500' : 'text-gray-400'}`}>
                        {charCount}/{MIN_COMMENT_LENGTH} characters minimum
                    </p>

                    <div className="flex justify-end gap-3 mt-4">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!isValidLength || isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmitting && (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                            Close Task
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClosureCommentModal;
